import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Home, Scroll, Wine, Star, Fish, Leaf, ChefHat, GlassWater, MapPin, Flame, Waves, Salad, Coffee, Cake, UtensilsCrossed, SlidersHorizontal } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Checkbox } from './ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';

const iconMap = {
  Home,
  Scroll,
  Wine,
  Star,
  Fish,
  Leaf,
  ChefHat,
  GlassWater,
  MapPin,
  Flame,
  Waves,
  Salad,
  Coffee,
  Cake,
  UtensilsCrossed,
};

const categories = [
  { id: 1, name: 'Home Cooked', icon: 'Home' },
  { id: 2, name: 'Food Tour', icon: 'MapPin' },
  { id: 3, name: 'Aperitivo', icon: 'GlassWater' },
  { id: 4, name: 'Supper Club', icon: 'Fish' },
  { id: 5, name: 'Farm to Table', icon: 'Leaf' },
  { id: 6, name: 'Cooking Class', icon: 'ChefHat' },
  { id: 7, name: 'Street Food', icon: 'MapPin' },
  { id: 8, name: 'BBQ & Grill', icon: 'Flame' },
];

const cuisineTypes = [
  'American', 'Chinese', 'French', 'Greek', 'Indian', 'Italian',
  'Japanese', 'Korean', 'Mediterranean', 'Mexican', 'Middle Eastern',
  'Spanish', 'Thai',
];

const mealTypes = ['Breakfast', 'Lunch', 'Dinner', 'All Day'];

const locations = [
  'New York, NY', 'Los Angeles, CA', 'San Francisco, CA', 'Chicago, IL',
  'Miami, FL', 'Austin, TX', 'Seattle, WA', 'Portland, OR', 'Boston, MA',
  'Denver, CO', 'Nashville, TN', 'New Orleans, LA', 'Brooklyn, NY',
  'Atlanta, GA', 'Philadelphia, PA', 'San Diego, CA',
  'Paris, France', 'London, UK', 'Rome, Italy', 'Barcelona, Spain',
  'Dubai, UAE', 'Abu Dhabi, UAE',
];

const CategoryBar = ({ selectedCategory, onCategorySelect, onFiltersChange, activeFilterCount = 0, showFilters = true }) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);

  const [priceRange, setPriceRange] = useState([0, 550]);
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [selectedMealTypes, setSelectedMealTypes] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 300);
    }
  };

  const toggleCuisine = (cuisine) => {
    setSelectedCuisines(prev =>
      prev.includes(cuisine) ? prev.filter(c => c !== cuisine) : [...prev, cuisine]
    );
  };

  const toggleMealType = (type) => {
    setSelectedMealTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const toggleLocation = (loc) => {
    setSelectedLocations(prev =>
      prev.includes(loc) ? prev.filter(l => l !== loc) : [...prev, loc]
    );
  };

  const applyFilters = () => {
    if (onFiltersChange) {
      onFiltersChange({
        priceRange,
        cuisines: selectedCuisines,
        mealTypes: selectedMealTypes,
        locations: selectedLocations,
      });
    }
    setSheetOpen(false);
  };

  const clearFilters = () => {
    setPriceRange([0, 550]);
    setSelectedCuisines([]);
    setSelectedMealTypes([]);
    setSelectedLocations([]);
    if (onFiltersChange) {
      onFiltersChange({
        priceRange: [0, 550],
        cuisines: [],
        mealTypes: [],
        locations: [],
      });
    }
    setSheetOpen(false);
  };

  const localFilterCount =
    (priceRange[0] > 0 || priceRange[1] < 550 ? 1 : 0) +
    selectedCuisines.length +
    selectedMealTypes.length +
    selectedLocations.length;

  return (
    <div className="sticky top-20 bg-[#F4EDE4] z-40 border-b border-gray-200" data-testid="category-bar">
      <div className="max-w-[2000px] mx-auto px-6 md:px-10 lg:px-20">
        <div className="flex items-center gap-4 py-4">
          {/* Categories Scroll */}
          <div className="relative flex-1 overflow-hidden">
            {showLeftArrow && (
              <button
                onClick={() => scroll('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-2 shadow-md hover:shadow-lg transition-shadow"
                data-testid="category-scroll-left"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}

            <div
              ref={scrollRef}
              onScroll={checkScroll}
              className="flex gap-6 md:gap-0 md:justify-around overflow-x-auto scrollbar-hide scroll-smooth px-2 w-full"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {categories.map((category) => {
                const IconComponent = iconMap[category.icon];
                const isSelected = selectedCategory === category.name;
                return (
                  <button
                    key={category.id}
                    onClick={() => onCategorySelect(isSelected ? null : category.name)}
                    className={`flex flex-col items-center gap-2 min-w-fit pb-3 border-b-2 transition-all ${
                      isSelected
                        ? 'border-gray-900 text-gray-900'
                        : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
                    }`}
                    data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {IconComponent && <IconComponent className="w-6 h-6" />}
                    <span className="text-xs font-medium whitespace-nowrap">{category.name}</span>
                  </button>
                );
              })}
            </div>

            {showRightArrow && (
              <button
                onClick={() => scroll('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-2 shadow-md hover:shadow-lg transition-shadow"
                data-testid="category-scroll-right"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center gap-1 sm:gap-2 rounded-xl border-gray-300 py-2 sm:py-3 px-2 sm:px-4 relative"
                    data-testid="filters-btn"
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                    <span className="font-medium hidden sm:inline">Filters</span>
                    {localFilterCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-[#D65A31] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold" data-testid="filter-count-badge">
                        {localFilterCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[380px] sm:w-[420px] overflow-y-auto" data-testid="filters-panel">
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                  </SheetHeader>
                  <div className="py-6 space-y-8">
                    {/* Price Range */}
                    <div>
                      <h3 className="font-semibold mb-4">Price range</h3>
                      <Slider
                        value={priceRange}
                        onValueChange={setPriceRange}
                        min={0}
                        max={550}
                        step={10}
                        className="mb-4"
                        data-testid="price-slider"
                      />
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>${priceRange[0]}</span>
                        <span>${priceRange[1]}{priceRange[1] >= 550 ? '+' : ''}</span>
                      </div>
                    </div>

                    {/* Cuisine Type */}
                    <div>
                      <h3 className="font-semibold mb-4">Cuisine</h3>
                      <div className="flex flex-wrap gap-2">
                        {cuisineTypes.map((cuisine) => (
                          <button
                            key={cuisine}
                            onClick={() => toggleCuisine(cuisine)}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                              selectedCuisines.includes(cuisine)
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                            }`}
                            data-testid={`filter-cuisine-${cuisine.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {cuisine}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Meal Type */}
                    <div>
                      <h3 className="font-semibold mb-4">Meal type</h3>
                      <div className="flex flex-wrap gap-2">
                        {mealTypes.map((type) => (
                          <button
                            key={type}
                            onClick={() => toggleMealType(type)}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                              selectedMealTypes.includes(type)
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                            }`}
                            data-testid={`filter-meal-${type.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Location */}
                    <div>
                      <h3 className="font-semibold mb-4">Location</h3>
                      <div className="space-y-2.5 max-h-52 overflow-y-auto pr-2">
                        {locations.map((loc) => (
                          <label
                            key={loc}
                            className="flex items-center gap-3 cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedLocations.includes(loc)}
                              onCheckedChange={() => toggleLocation(loc)}
                              data-testid={`filter-location-${loc.toLowerCase().replace(/[\s,]+/g, '-')}`}
                            />
                            <span className="text-sm">{loc}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Footer actions */}
                  <div className="sticky bottom-0 bg-white border-t pt-4 pb-2 flex items-center justify-between gap-3">
                    <button
                      onClick={clearFilters}
                      className="text-sm font-medium underline"
                      data-testid="clear-filters-btn"
                    >
                      Clear all
                    </button>
                    <Button
                      onClick={applyFilters}
                      className="bg-[#D65A31] hover:bg-[#B84A28] text-white px-6"
                      data-testid="apply-filters-btn"
                    >
                      Show results
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryBar;
